import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { loadProperties, loadPropertyDetails, saveProperties } from "../../actionCreators";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropertyComponent from "./PropertyComponent";
import { getProperties } from "../../selectors";
import DescriptionIcon from "@mui/icons-material/Description";
import { Grid } from "@mui/material";
import { Button, CollapseCard } from "@totalsoft/rocket-ui";
import { useReactiveVar } from "@apollo/client";
import { synchronizationInProgressVar } from "variables";

const PropertyContainer = ({ actions, partnerId, properties, propertiesLoaded, setIsDirty, hasWritePermission }) => {
  const { t } = useTranslation();
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);

  const [localProperties, setPropertyList] = useState(properties);
  const [cardExpanded, setExpanded] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);

  useEffect(() => {
    if (cardExpanded && !propertiesLoaded && partnerId !== null) actions.loadProperties(partnerId);
  }, [cardExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (propertiesLoaded) {
      setPropertyList(properties);
    }
  }, [propertiesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const onExpand = expanded => {
    if (cardExpanded !== expanded) setExpanded(expanded);
  };

  const loadPropertyDetails = propId => {
    return actions.loadPropertyDetails(propId).then(data => {
      return !data || data === {} ? [] : data;
    });
  };

  const onPropertyChange = idx => prop => value => {
    setPropertyList(localProperties =>
      Object.assign([], {
        ...localProperties,
        [idx]: {
          ...localProperties[idx],
          [prop]: value
        }
      })
    );
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const saveProperties = (partnerId, localProperties) => {
    if (!hasWritePermission) return;
    actions.saveProperties(partnerId, localProperties, () => {
      setIsDirty(false);
    });
    setSaveDisabled(true);
  };

  return (
    <CollapseCard
      id="propertyCard"
      icon={DescriptionIcon}
      iconColor="primary"
      title={t("Partner.Properties")}
      expanded={cardExpanded}
      onToggle={(_, expanded) => onExpand(!expanded)}
      defaultExpanded={synchronizationInProgress}
      toggleOnHeaderClick
    >
      {localProperties && localProperties.length > 0 ? (
        <Grid container spacing={2}>
          {localProperties.map((item, idx) => {
            return (
              <PropertyComponent
                key={idx}
                idx={idx}
                item={item}
                loadPropertyDetails={loadPropertyDetails}
                onPropertyChange={onPropertyChange}
                setSaveDisabled={setSaveDisabled}
                hasWritePermission={hasWritePermission}
              />
            );
          })}
          <Grid item xs={12} sm={12} md={12}>
            <Button
              id="property_saveBtn"
              color="primary"
              variant="contained"
              onClick={() => saveProperties(partnerId, localProperties)}
              disabled={!hasWritePermission || saveDisabled}
            >
              {t("Save")}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <span>{""}</span>
      )}
    </CollapseCard>
  );
};

PropertyContainer.propTypes = {
  actions: PropTypes.object.isRequired,
  partnerId: PropTypes.number,
  properties: PropTypes.array,
  propertiesLoaded: PropTypes.bool.isRequired,
  propertiesLoading: PropTypes.bool.isRequired,
  setIsDirty: PropTypes.func,
  hasWritePermission: PropTypes.bool.isRequired
};

function mapStateToProps(state, ownProps) {
  const {
    content: properties,
    loaded: propertiesLoaded,
    loading: propertiesLoading
  } = getProperties(state, ownProps.partnerId);

  return {
    properties,
    propertiesLoaded,
    propertiesLoading
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadProperties,
        loadPropertyDetails,
        saveProperties
      },
      dispatch
    ),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PropertyContainer);
